/**
 * Sub-components extracted from DvrRevisions.jsx (Iter33 P2 refactor).
 *
 * All are pure presentational/form components — no shared state with the
 * parent DvrRevisions screen except via props. Hooks used inside each
 * component are local and follow the Rules of Hooks.
 */
import { useEffect, useState } from "react";
import { ClipboardSignature, GitBranch, Plus, Save } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  SIGNATURE_ROLE_LABELS,
  DATA_CERTA_LABELS,
  STATUS_LABELS,
  STATUS_CLASSES,
  CHANGE_CLASSES,
  CHANGE_LABELS,
  EMPTY_SIGNATURE,
} from "./DvrRevisions.constants";


export function StatusBadge({ status, label }) {
  return (
    <Badge
      className={STATUS_CLASSES[status] || "bg-slate-100"}
      data-testid={`revision-status-badge-${status}`}
    >
      {label || STATUS_LABELS[status] || status}
    </Badge>
  );
}


export function ChangedSectionsList({ items }) {
  if (!items?.length) {
    return <p className="text-sm text-slate-500">Nessuna sezione confrontata.</p>;
  }
  return (
    <ul className="space-y-2" data-testid="revision-changed-sections">
      {items.map((s) => (
        <li
          key={s.section_key}
          className={`flex items-start justify-between gap-3 rounded-md border px-3 py-2 text-sm ${CHANGE_CLASSES[s.change_type] || ""}`}
          data-testid={`changed-section-${s.section_key}`}
        >
          <div className="flex-1">
            <div className="font-semibold">{s.section_title}</div>
            <div className="text-xs">{s.summary}</div>
          </div>
          <Badge variant="outline" className="border-current">
            {CHANGE_LABELS[s.change_type] || s.change_type}
          </Badge>
        </li>
      ))}
    </ul>
  );
}


export function SignaturePanel({ open, onClose, onSave, saving }) {
  const [form, setForm] = useState(EMPTY_SIGNATURE);
  useEffect(() => { if (open) setForm(EMPTY_SIGNATURE); }, [open]);
  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const submit = (e) => { e.preventDefault(); onSave(form); };
  const isConsultative = form.role === "rls" || form.role === "rlst";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="signature-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardSignature className="h-5 w-5" /> Aggiungi firma · Data certa
          </DialogTitle>
          <DialogDescription>
            Registra una firma sul DVR con il relativo metodo di "data certa" ex art. 28 c.2 D.Lgs. 81/2008.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Ruolo firmatario</Label>
            <select
              className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
              value={form.role}
              onChange={(e) => update("role", e.target.value)}
              data-testid="signature-role-select"
            >
              {Object.entries(SIGNATURE_ROLE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            {isConsultative && (
              <p className="text-xs text-blue-700">RLS/RLST: registrata come consultazione / presa visione.</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Nome e cognome firmatario</Label>
            <Input
              required
              value={form.signer_name}
              onChange={(e) => update("signer_name", e.target.value)}
              data-testid="signature-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Codice fiscale (opzionale)</Label>
            <Input
              value={form.fiscal_code}
              onChange={(e) => update("fiscal_code", e.target.value.toUpperCase())}
              data-testid="signature-cf-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Metodo data certa</Label>
            <select
              className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
              value={form.data_certa_method}
              onChange={(e) => update("data_certa_method", e.target.value)}
              data-testid="signature-data-certa-method-select"
            >
              {Object.entries(DATA_CERTA_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label>Riferimento data certa</Label>
            <Input
              placeholder="es. n° protocollo, ID PEC, timestamp QTSP"
              value={form.data_certa_reference}
              onChange={(e) => update("data_certa_reference", e.target.value)}
              data-testid="signature-data-certa-reference-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Data certa</Label>
            <Input
              type="date"
              value={form.data_certa_date || ""}
              onChange={(e) => update("data_certa_date", e.target.value)}
              data-testid="signature-data-certa-date-input"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Note</Label>
            <textarea
              className="min-h-16 w-full rounded-md border border-slate-200 p-2 text-sm"
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              data-testid="signature-notes-textarea"
            />
          </div>
          <DialogFooter className="md:col-span-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="signature-cancel-button">
              Annulla
            </Button>
            <Button type="submit" disabled={saving} className="bg-blue-900 hover:bg-blue-800" data-testid="signature-save-button">
              <Save className="h-4 w-4 mr-1" /> Registra firma
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


export function CreateRevisionDialog({ open, onClose, onCreate, saving }) {
  const [reason, setReason] = useState("");
  const [docDate, setDocDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  useEffect(() => {
    if (open) {
      setReason("");
      setDocDate(new Date().toISOString().slice(0, 10));
      setNotes("");
    }
  }, [open]);
  const submit = (e) => {
    e.preventDefault();
    if (!reason.trim() || reason.trim().length < 2) {
      toast.error("Inserisci una motivazione (minimo 2 caratteri)");
      return;
    }
    onCreate({ reason, document_date: docDate, notes, clone_from_previous: true });
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="create-revision-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitBranch className="h-5 w-5" /> Nuova revisione DVR
          </DialogTitle>
          <DialogDescription>
            I dati della revisione precedente saranno clonati automaticamente.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-2">
            <Label>Motivo della revisione *</Label>
            <textarea
              required
              className="min-h-20 w-full rounded-md border border-slate-200 p-2 text-sm"
              placeholder="es. Aggiornamento annuale, nuovo ATECO, infortunio, modifica organico…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="revision-reason-textarea"
            />
          </div>
          <div className="space-y-2">
            <Label>Data documento</Label>
            <Input
              type="date"
              value={docDate}
              onChange={(e) => setDocDate(e.target.value)}
              data-testid="revision-document-date-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Note (opzionali)</Label>
            <textarea
              className="min-h-16 w-full rounded-md border border-slate-200 p-2 text-sm"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="revision-notes-textarea"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="create-revision-cancel-button">
              Annulla
            </Button>
            <Button type="submit" disabled={saving} className="bg-blue-900 hover:bg-blue-800" data-testid="create-revision-submit-button">
              <Plus className="h-4 w-4 mr-1" /> Crea revisione
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
